import BaseApiService from './BaseApiService'
import type { PlatformAnalytics } from './analyticsApiService'
import type { AdminCounts, AdminJobsResponse, AdminUserFilters, AdminTeam, AdminUser, ApiKey, DeferralSummary, LogEntry, LogQuery, PagedResult } from '../types'

class AdminApiService extends BaseApiService {
  getTeams(page = 1, pageSize = 20): Promise<PagedResult<AdminTeam>> {
    return this.post<PagedResult<AdminTeam>>('/v1/admin/teams', { page, pageSize })
  }

  /** Marks a team as not a customer. A reason is required to grant one; none to revoke. */
  setTeamBillingExempt(teamId: string, exempt: boolean, reason?: string): Promise<void> {
    return this.post<void>('/v1/admin/set-team-billing-exempt', { teamId, exempt, reason })
  }

  /** Tiers are assigned and sticky; this is the only way onto Enterprise. */
  setTeamPlan(teamId: string, planCode: string): Promise<void> {
    return this.post<void>('/v1/admin/set-team-plan', { teamId, planCode })
  }

  /** What assigning Free would defer (failed / carried periods, unbilled extras). Read-only; shown before set-team-plan. */
  getFreeDeferralPreview(teamId: string): Promise<DeferralSummary> {
    return this.post<DeferralSummary>('/v1/admin/free-deferral-preview', { teamId })
  }

  /** Negotiated concurrent-render limit (min–max); null restores the plan default. Super-admin only. */
  setTeamRenderConcurrency(teamId: string, limit: number | null): Promise<{ teamId: string; renderConcurrency: RenderConcurrency }> {
    return this.post('/v1/admin/set-team-render-concurrency', { teamId, limit })
  }

  getUsers(page = 1, pageSize = 20, filters: AdminUserFilters = {}): Promise<PagedResult<AdminUser>> {
    return this.post<PagedResult<AdminUser>>('/v1/admin/users', { page, pageSize, ...filters })
  }

  getUser(userId: string): Promise<AdminUser> {
    return this.post<AdminUser>('/v1/admin/user', { userId })
  }

  lockUser(userId: string): Promise<void> {
    return this.post<void>('/v1/admin/lock-user', { userId })
  }

  /** Lost-device recovery: turns the user's MFA off, discards secret + recovery codes, signs them out everywhere. */
  resetUserMfa(userId: string): Promise<void> {
    return this.post<void>('/v1/admin/reset-user-mfa', { userId })
  }

  unlockUser(userId: string): Promise<void> {
    return this.post<void>('/v1/admin/unlock-user', { userId })
  }

  /** Application logs, newest first. Admin/root only — rows carry every tenant's request URLs. */
  getLogs(query: LogQuery = {}): Promise<LogEntry[]> {
    return this.post<LogEntry[]>('/v1/admin/logs', {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 100,
      minimumLevel: query.minimumLevel ?? null,
      since: query.since ?? null,
      search: query.search ?? null,
      minimumHttpStatus: query.minimumHttpStatus ?? null,
      source: query.source ?? null,
      jobName: query.jobName ?? null,
      jobRunId: query.jobRunId ?? null,
    })
  }

  /** Size, per-level/age row counts, retention window and last cleanup run for the "Clean up logs" panel. */
  getLogStorage(): Promise<LogStorage> {
    return this.post<LogStorage>('/v1/admin/logs/storage')
  }

  /**
   * One purge request. "olderThan" is bounded per request — repeat with `before: result.cutoff` while
   * `moreRemaining`. "all" needs the exact confirmation phrase.
   */
  purgeLogs(req: PurgeLogsRequest): Promise<PurgeLogsResult> {
    return this.post<PurgeLogsResult>('/v1/admin/logs/purge', req)
  }

  /** 1–365 days, or null to go back to the configured default. */
  setLogRetention(days: number | null): Promise<LogRetention> {
    return this.post<LogRetention>('/v1/admin/logs/set-retention', { days })
  }

  getJobs(): Promise<AdminJobsResponse> {
    return this.post<AdminJobsResponse>('/v1/admin/jobs')
  }

  getDashboard(): Promise<AdminCounts> {
    return this.post<AdminCounts>('/v1/admin/dashboard')
  }

  getPlatformAnalytics(days = 30): Promise<PlatformAnalytics> {
    return this.post<PlatformAnalytics>('/v1/admin/analytics/platform', { days })
  }

  getTeamApiKeys(teamId: string): Promise<ApiKey[]> {
    return this.post<ApiKey[]>('/v1/admin/team-api-keys', { teamId })
  }

  /** What an ad-hoc charge has to work with: provider status and the team's default card (display-only). */
  getTeamBillingDetail(teamId: string): Promise<TeamBillingDetail> {
    return this.post<TeamBillingDetail>('/v1/admin/team-billing-detail', { teamId })
  }

  /** Charges the team's default card an arbitrary amount, outside the monthly cycle. Super-admin only. */
  chargeTeamAdHoc(teamId: string, amountCents: number, description: string, currency = 'usd'): Promise<AdHocChargeResult> {
    return this.post<AdHocChargeResult>('/v1/admin/charge-team-adhoc', { teamId, amountCents, description, currency })
  }

  getPaymentMethods(teamId: string): Promise<AdminPaymentMethod[]> {
    return this.post<AdminPaymentMethod[]>('/v1/admin/payment-methods', { teamId })
  }

  setDefaultPaymentMethod(teamId: string, billingPaymentMethodId: string): Promise<void> {
    return this.post<void>('/v1/admin/set-default-payment-method', { teamId, billingPaymentMethodId })
  }

  removePaymentMethod(teamId: string, billingPaymentMethodId: string): Promise<void> {
    return this.post<void>('/v1/admin/remove-payment-method', { teamId, billingPaymentMethodId })
  }

  getPaymentHistory(teamId: string): Promise<PaymentHistory> {
    return this.post<PaymentHistory>('/v1/admin/payment-history', { teamId })
  }

  refundCharge(billingChargeId: string, amountCents: number | null, reason: string | null): Promise<RefundResult> {
    return this.post<RefundResult>('/v1/admin/refund-charge', { billingChargeId, amountCents, reason })
  }

  /** Grants (or, negative, debits) account credit that offsets the next metered invoice. */
  grantCredit(teamId: string, amountCents: number, reason: string | null): Promise<{ creditBalanceCents: number }> {
    return this.post<{ creditBalanceCents: number }>('/v1/admin/grant-credit', { teamId, amountCents, reason })
  }

  getBillingPeriods(teamId: string): Promise<BillingPeriodsResult> {
    return this.post<BillingPeriodsResult>('/v1/admin/billing-periods', { teamId })
  }

  /** Manually retries collection on an unpaid period, now (bypasses the dunning schedule). */
  retryPeriod(billingPeriodId: string): Promise<{ succeeded: boolean; processing: boolean; failureMessage: string | null }> {
    return this.post('/v1/admin/retry-period', { billingPeriodId })
  }

  /** Clears a suspension / failure state so the team can render again and dunning starts fresh. */
  resumeBilling(teamId: string): Promise<void> {
    return this.post<void>('/v1/admin/resume-billing', { teamId })
  }

  /** Issues a Stripe-hosted invoice to a team; returns the hosted URL. */
  createInvoice(teamId: string, lines: { description: string; amountCents: number }[], memo: string | null): Promise<CreateInvoiceResult> {
    return this.post<CreateInvoiceResult>('/v1/admin/create-invoice', { teamId, lines, memo })
  }

  listInvoices(teamId: string): Promise<AdminInvoice[]> {
    return this.post<AdminInvoice[]>('/v1/admin/list-invoices', { teamId })
  }

  voidInvoice(billingInvoiceId: string): Promise<{ status: string }> {
    return this.post<{ status: string }>('/v1/admin/void-invoice', { billingInvoiceId })
  }
}

export interface AdminInvoice {
  billingInvoiceId: string
  providerInvoiceRef: string
  hostedInvoiceUrl: string | null
  amountCents: number
  currency: string
  status: string
  description: string | null
  createdAt: string
}

export interface CreateInvoiceResult {
  invoiceId: string
  invoiceRef: string
  hostedUrl: string | null
  status: string
  amountCents: number
}

export interface BillingPeriodRow {
  billingPeriodId: string
  periodStart: string
  periodEnd: string
  status: number
  totalCents: number
  currency: string
}

export interface BillingPeriodsResult {
  periods: BillingPeriodRow[]
  billingStatus: number
  failedAttemptCount: number
  firstFailedAt: string | null
}

export interface AdminPaymentMethod {
  billingPaymentMethodId: string
  provider: string
  providerIdentifier: string
  methodType: string
  verified: boolean
  brand: string | null
  last4: string | null
  expiryMonth: number | null
  expiryYear: number | null
  isDefault: boolean
  createdAt: string
}

export interface BillingChargeRow {
  billingChargeId: string
  billingPeriodId: string | null
  amountCents: number
  currency: string
  status: number
  description: string | null
  failureCode: string | null
  attemptedAt: string
  providerChargeIdentifier: string | null
}

export interface BillingRefundRow {
  billingRefundId: string
  billingChargeId: string
  amountCents: number
  currency: string
  status: number
  reason: string | null
  createdAt: string
}

export interface BillingCreditRow {
  billingCreditId: string
  amountCents: number
  currency: string
  reason: string | null
  appliedToPeriodId: string | null
  createdAt: string
}

export interface PaymentHistory {
  charges: BillingChargeRow[]
  refunds: BillingRefundRow[]
  credits: BillingCreditRow[]
  creditBalanceCents: number
}

export interface RefundResult {
  refundId: string
  status: number
  succeeded: boolean
  pending: boolean
  providerRefundRef: string | null
  amountCents: number
  failureMessage: string | null
}

export interface TeamBillingDetail {
  teamId: string
  teamName: string
  planCode: string
  billingExempt: boolean
  hasCustomer: boolean
  providerEnabled: boolean
  card: { brand: string | null; last4: string | null; expiryMonth: number | null; expiryYear: number | null } | null
  renderConcurrency: RenderConcurrency
  /** The team's own overage settings, read-only here: overage billed, and its monthly limit in whole dollars (null = none). */
  allowOverage?: boolean
  overageLimitDollars?: number | null
}

/** A team's cap on concurrent synchronous renders: plan default, negotiated override (null = none), and the one in force. */
export interface RenderConcurrency {
  planDefault: number
  override: number | null
  effective: number
  min: number
  max: number
}

export interface AdHocChargeResult {
  chargeId: string
  succeeded: boolean
  status: number
  providerChargeRef: string | null
  amountCents: number
  currency: string
  failureCode: string | null
  failureMessage: string | null
}

export interface LogRetention {
  days: number
  overrideDays: number | null
  defaultDays: number
  minDays: number
  maxDays: number
  updatedAt: string | null
  updatedBy: string | null
}

export interface LogLevelRow {
  level: string
  rows: number
  last24Hours: number
  days1To7: number
  days7To30: number
  days30To90: number
  over90Days: number
  approxKb: number
}

export interface LogStorage {
  generatedAt: string
  stats: {
    approxRows: number
    reservedKb: number
    usedKb: number
    databaseUsedKb: number
    databaseMaxKb: number | null
    oldestAt: string | null
    newestAt: string | null
  }
  levels: LogLevelRow[] | null
  breakdownAvailable: boolean
  retention: LogRetention
  lastCleanup: {
    jobName: string
    intervalSeconds: number
    lastStartedAt: string | null
    lastFinishedAt: string | null
    lastOutcome: string | null
    lastSummary: string | null
    lastRunId: string | null
    nextDueAt: string | null
  }
  confirmPhrase: string
}

export interface PurgeLogsRequest {
  mode: 'olderThan' | 'all'
  olderThanDays?: number
  before?: string
  levels?: string[]
  confirm?: string
}

export interface PurgeLogsResult {
  mode: string
  cutoff: string | null
  levels: string[] | null
  deleted: number
  moreRemaining: boolean
  truncated: boolean
  fallback: boolean
}

export const adminApiService = new AdminApiService()
